import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { Lock, DollarSign, CheckCircle, XCircle, MessageSquare } from 'lucide-react';

const AdminOffers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [respondDialogOpen, setRespondDialogOpen] = useState(false);
  const [responseData, setResponseData] = useState({
    action: 'accept',
    admin_response: '',
    counter_amount: ''
  });
  const navigate = useNavigate();

  // Simple password protection
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const ADMIN_PASSWORD = 'Gunrule#54';

  useEffect(() => {
    if (isAuthenticated) {
      fetchOffers();
    }
  }, [filter, isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      toast.success('Welcome to Admin Dashboard');
    } else {
      toast.error('Invalid password');
    }
  };

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const statusParam = filter !== 'all' ? `?status=${filter}` : '';
      const response = await api.get(`/offers${statusParam}`);
      
      if (response.data.success) {
        setOffers(response.data.offers);
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  const openRespondDialog = (offer) => {
    setSelectedOffer(offer);
    setResponseData({
      action: 'accept',
      admin_response: '',
      counter_amount: ''
    });
    setRespondDialogOpen(true);
  };

  const handleRespond = async (e) => {
    e.preventDefault();
    
    if (!responseData.admin_response.trim()) {
      toast.error('Please enter a response message');
      return;
    }

    if (responseData.action === 'counter' && !responseData.counter_amount) {
      toast.error('Please enter a counter amount');
      return;
    }

    try {
      const payload = {
        action: responseData.action,
        admin_response: responseData.admin_response,
        counter_amount: responseData.counter_amount ? parseFloat(responseData.counter_amount) : null
      };

      const response = await api.post(`/offers/${selectedOffer.id}/respond`, payload);
      
      if (response.data.success) {
        toast.success(`Offer ${responseData.action}ed successfully!`);
        setRespondDialogOpen(false);
        fetchOffers(); // Refresh the list
      }
    } catch (error) {
      console.error('Error responding to offer:', error);
      toast.error('Failed to respond to offer');
    }
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      countered: 'bg-blue-100 text-blue-800',
      completed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full border-4 border-charcoal">
          <div className="flex justify-center mb-6">
            <Lock className="h-16 w-16 text-charcoal" />
          </div>
          <h1 className="text-3xl font-black text-charcoal text-center mb-6 uppercase">
            Admin Login
          </h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="password" className="text-charcoal font-bold">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="mt-1"
                required
              />
            </div>
            <Button type="submit" className="w-full bg-charcoal text-cream hover:bg-charcoal/90 font-bold">
              Login
            </Button>
            <Button 
              type="button" 
              onClick={() => navigate('/')} 
              variant="outline" 
              className="w-full"
            >
              Back to Home
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-charcoal text-cream p-6 border-b-4 border-warmSage">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-3xl font-black uppercase">Hook It Up - Admin Dashboard</h1>
          <Button onClick={() => navigate('/')} variant="outline" className="text-cream border-cream hover:bg-cream hover:text-charcoal">
            Back to Site
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {['all', 'pending', 'accepted', 'countered', 'rejected'].map((status) => (
            <Button
              key={status}
              onClick={() => setFilter(status)}
              variant={filter === status ? 'default' : 'outline'}
              className={`uppercase font-bold ${
                filter === status 
                  ? 'bg-warmSage text-charcoal' 
                  : 'border-charcoal text-charcoal hover:bg-warmSage/20'
              }`}
            >
              {status}
            </Button>
          ))}
        </div>

        {/* Offers List */}
        {loading ? (
          <div className="text-center py-16">
            <p className="text-2xl text-charcoal font-bold">Loading offers...</p>
          </div>
        ) : offers.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-2xl text-warmGray">No offers found</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {offers.map((offer) => (
              <div key={offer.id} className="bg-white p-6 rounded-lg shadow-lg border-4 border-charcoal">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-2xl font-black text-charcoal mb-2">{offer.product_name}</h3>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold uppercase ${getStatusBadgeColor(offer.status)}`}>
                      {offer.status}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-warmGray">Offer ID</p>
                    <p className="text-sm font-mono text-charcoal">{offer.id.substring(0, 8)}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-warmGray">Customer</p>
                    <p className="text-lg font-bold text-charcoal">{offer.customer_name}</p>
                    <p className="text-sm text-blue-600">{offer.customer_email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-warmGray">Submitted</p>
                    <p className="text-sm text-charcoal">{new Date(offer.created_at).toLocaleString()}</p>
                  </div>
                </div>

                <div className="bg-gray-100 p-4 rounded mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-warmGray">Original Price:</span>
                    <span className="text-lg font-bold text-charcoal">${offer.original_price.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-warmGray">Offer Amount:</span>
                    <span className="text-2xl font-black text-blue-600">${offer.offer_amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-300">
                    <span className="text-sm text-warmGray">Discount:</span>
                    <span className="text-lg font-bold text-red-600">
                      {(((offer.original_price - offer.offer_amount) / offer.original_price) * 100).toFixed(1)}% off
                    </span>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-warmGray mb-1">Customer Message:</p>
                  <p className="text-charcoal italic">{offer.message}</p>
                </div>

                {offer.admin_response && (
                  <div className="bg-blue-50 p-4 rounded mb-4 border-2 border-blue-200">
                    <p className="text-sm text-blue-600 font-bold mb-1">Your Response:</p>
                    <p className="text-charcoal">{offer.admin_response}</p>
                    {offer.counter_amount && (
                      <p className="text-lg font-bold text-blue-600 mt-2">
                        Counter Offer: ${offer.counter_amount.toFixed(2)}
                      </p>
                    )}
                  </div>
                )}

                {offer.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openRespondDialog(offer)}
                      className="flex-1 bg-warmSage text-charcoal hover:bg-warmSage/90 font-bold"
                    >
                      <MessageSquare className="h-5 w-5 mr-2" />
                      Respond to Offer
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Respond Dialog */}
      <Dialog open={respondDialogOpen} onOpenChange={setRespondDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-charcoal">
              Respond to Offer
            </DialogTitle>
          </DialogHeader>

          {selectedOffer && (
            <form onSubmit={handleRespond} className="space-y-4 mt-4">
              <div className="bg-gray-100 p-3 rounded">
                <p className="text-sm text-warmGray">Product</p>
                <p className="font-bold text-charcoal">{selectedOffer.product_name}</p>
                <p className="text-sm mt-2">
                  <span className="text-warmGray">Customer Offer: </span>
                  <span className="font-bold text-blue-600">${selectedOffer.offer_amount.toFixed(2)}</span>
                </p>
              </div>

              <div>
                <Label htmlFor="action" className="text-charcoal font-bold">Response Type *</Label>
                <select
                  id="action"
                  value={responseData.action}
                  onChange={(e) => setResponseData({ ...responseData, action: e.target.value })}
                  className="w-full mt-1 p-2 border-2 border-charcoal rounded"
                  required
                >
                  <option value="accept">Accept Offer</option>
                  <option value="counter">Counter Offer</option>
                  <option value="reject">Reject Offer</option>
                </select>
              </div>

              {responseData.action === 'counter' && (
                <div>
                  <Label htmlFor="counter_amount" className="text-charcoal font-bold">Counter Amount *</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <Input
                      id="counter_amount"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={responseData.counter_amount}
                      onChange={(e) => setResponseData({ ...responseData, counter_amount: e.target.value })}
                      className="pl-7"
                      required
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="admin_response" className="text-charcoal font-bold">Your Message *</Label>
                <Textarea
                  id="admin_response"
                  placeholder="Write a personal message to the customer..."
                  value={responseData.admin_response}
                  onChange={(e) => setResponseData({ ...responseData, admin_response: e.target.value })}
                  rows={4}
                  className="mt-1"
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold"
              >
                Send Response
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOffers;
